import { useState } from 'react'
import { ChevronDown, Plus } from 'lucide-react'

import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { ItemList } from './ItemList'
import { currencyFormatter } from '../../../utils/utils'

/** Section header strip + collapsible body for the per-section line items
 *  (Calibrations / Services / Parts / Labor) on the summary pages. The header
 *  shows section title + count chip + total, and a [+] action that opens the
 *  manage drawer. Item rows render in the body when expanded. */
const ExpandableSection = ({
  title,
  total,
  items,
  onManage,
  disabled: disabledProp,
  isNational,
  canManage = true,
  defaultExpanded = true,
  onEdit,
  type
}) => {
  // Folded in here as well as at the call sites: the [+] that opens the manage drawer
  // is the add path for calibrations, services, parts and labour.
  const readOnlyUser = useIsReadOnly()
  const disabled = disabledProp || readOnlyUser
  const [expanded, setExpanded] = useState(!!defaultExpanded)
  const itemCount = Array.isArray(items) ? items.length : 0

  return (
    <div className='mb-3 overflow-hidden rounded-lg border border-[#D1D5D8] bg-white'>
      {/* Lavender section header */}
      <button
        type='button'
        onClick={() => setExpanded((e) => !e)}
        className='flex w-full items-center justify-between gap-3 bg-[#E8EFFF] px-4 py-2.5 text-left'
        aria-expanded={expanded}
      >
        <div className='flex items-center gap-2'>
          <ChevronDown className={`h-4 w-4 text-[#475569] transition-transform ${expanded ? '' : '-rotate-90'}`} strokeWidth={2.25} />
          <span className='text-[14px] font-semibold text-[#0A1628]'>{title}</span>
          <span className='inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#0A1628] px-1.5 text-[11px] font-bold text-white'>
            {itemCount}
          </span>
        </div>
        <div className='flex items-center gap-3'>
          <span className='text-[14px] font-semibold text-[#0A1628]'>{currencyFormatter(total)}</span>
          {canManage && (
            <span
              role='button'
              tabIndex={disabled ? -1 : 0}
              aria-label={`Add to ${title}`}
              onClick={(e) => {
                e.stopPropagation()
                if (!disabled) onManage?.()
              }}
              onKeyDown={(e) => {
                if (disabled) return
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onManage?.()
                }
              }}
              className={`inline-flex h-7 w-7 items-center justify-center rounded border border-[#D1D5D8] bg-white text-[#0A1628] transition-colors hover:bg-[#F8FAFC] ${
                disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
              }`}
            >
              <Plus className='h-4 w-4' />
            </span>
          )}
        </div>
      </button>

      {/* Body */}
      {expanded && (
        <AdasBox className='px-4 py-1'>
          {itemCount === 0 ? (
            <AdasTypography className='py-4 text-center text-[13px] text-[#6B7280]'>No items added yet</AdasTypography>
          ) : (
            <ItemList itemList={items} name={title} onClickEdit={onEdit} type={type} disabled={disabled} isNational={isNational} />
          )}
        </AdasBox>
      )}
    </div>
  )
}

export default ExpandableSection
